/* eslint-disable react-hooks/exhaustive-deps */
import { Row, Col, message, Button, Modal, Card, Input, Space, Divider, Typography } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';

import ProfileSummary from '../components/layout/AccountSummary/ProfileSummary';
import API from '../services/API';
import { Tabs } from 'antd';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { parseUserDetailsPayload, handleCreditCardPayload } from '../helpers/parsingHelper';
import MoneyTransactions from '../components/layout/AccountSummary/MoneyTransactions';
import Referrals from '../components/layout/AccountSummary/ReferralTab';
import ApiState from '../helpers/ApiState';

import FurnishmentDetails from '../components/layout/AccountSummary/FurnishmentDetails';
import {isSupportManager, isSupportAgent2, isDev} from "../helpers/genericHelper";
import PlaidDetails from "../components/layout/AccountSummary/Plaid/PlaidDetails";
import FirebaseDeviceEvents from "../components/layout/AccountSummary/FirebaseDeviceEvents";
import Products from '../components/layout/AccountSummary/Products';
import SubscriptionDetails from '../components/layout/AccountSummary/SubscriptionDetails';
import { RetweetOutlined } from '@ant-design/icons';
import useScrollRestoration from '../helpers/useScrollRestoration';
import StickyTabBar from '../components/StickyTabBar';
import { addRecentUser } from '../helpers/recentUsers';


const { TabPane } = Tabs;
const { Text } = Typography;
const APP_TITLE = "Ava Admin";
const PROFILE_SUMMARY_TAB = "1";
// Must match the TabPane keys below, or a link to a new tab falls back to Profile Summary
const ACCOUNT_TABS = [PROFILE_SUMMARY_TAB, "2", "3", "4", "5", "6", "8", "9"];

function AccountSummary() {
  const UPDATE_TIMEOUT = 100;
  const [summaryData, setSummaryData] = useState(initialDataState);
  const [firebaseInfo, setFirebaseInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteAccountReason, setDeleteAccountReason] = useState(null);
  const [ccAccounts, setCcAccounts] = useState(null);
  const [ccDomains, setCcDomain] = useState(null);
  const [creditCards, setCreditCards] = useState(null);

  const [loanDetails, setLoanDetails] = useState({});
  
  const [customer, setCustomer] = useState([]);
  const [charges, setCharges] = useState([]);
  const [disputes, setDisputes] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [autoPayStatus, setAutoPayStatus] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  let { userId } = useParams();
  const history = useHistory();
  const location = useLocation();

  // The open tab lives in the URL so a page refresh stays on it
  const tabFromUrl = new URLSearchParams(location.search).get('tab');
  const activeTab = ACCOUNT_TABS.includes(tabFromUrl) ? tabFromUrl : PROFILE_SUMMARY_TAB;

  const restoreScrollPosition = useScrollRestoration(`${userId}:${activeTab}`);

  const tabBarAnchorRef = useRef(null);

  const changeTab = (tab) => {
    // Go back to where this tab was left, or to its top if it hasn't been scrolled yet
    const restored = restoreScrollPosition(`${userId}:${tab}`);
    if (!restored && tabBarAnchorRef.current?.getBoundingClientRect().top < 0) {
      tabBarAnchorRef.current.scrollIntoView();
    }

    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', tab);
    history.replace({ ...location, search: `?${searchParams.toString()}` });
  };

  const paymentAttempts = ApiState({
    apiCall: async () => await API.getPaymentAttempts(userId),
    loadOnInit: false,
    additionalParams: { userId }
  })

  useEffect(() => {
    // Clear the previous user's details, so they can't show next to this user's ID
    // while loading, or after this user's details fail to load
    setSummaryData(initialDataState);
    getAllData();
  }, [userId]);

  useEffect(() => {
    setCreditCards(handleCreditCardPayload(ccAccounts || [], ccDomains || []));
  }, [ccDomains, ccAccounts]);

  const loadLoanDetails = async () => {
    const list = {};
    const loans = await API.getLoanDetails({ userId: userId });
    loans.forEach(async (loanDetails) => {
      const loanStatements = await API.getLoanStatements({ loanId: loanDetails.id });
      list[loanDetails.id] = {
        loanDetails,
        loanStatements,
      };
    });
    setLoanDetails(list);
  }

  const getAllData = () => {
    getData();
    allCharges();
    allInvoices();
    getCustomer();
    getFirebaseInfo();
    getCCDomains();
    getCcAccounts();
    loadLoanDetails();
    getAutoPayStatus();
  }

  // Reloads everything on the page after a change. Tabs that have been opened are rebuilt
  // through refreshKey because many of them load their own data when they mount.
  const refreshAccount = () => {
    restoreScrollPosition(`${userId}:${activeTab}`);
    setRefreshKey((key) => key + 1);
    getAllData();
  }

  const getAutoPayStatus = async () => {
    const res = await API.getAutoPayStatus({ userId });
    setAutoPayStatus(res);
  }

  const getData = async () => {
    setLoading(true);
    try {
      let res = await API.getUserStats(userId);
      let parsedPayload = parseUserDetailsPayload(res);
      setSummaryData(parsedPayload);
      addRecentUser({ ...parsedPayload.userSummary, userId });
    } catch (error) {
      message.error('Unable to fetch User Details');
    } finally {
      setLoading(false);
    }
  }

  const getFirebaseInfo = async () => {
    try {
      const res = await API.getFirebaseLoginInfo({userId});
      setFirebaseInfo(res);
    } catch (error) {
      console.error('Unable to fetch Firebase Info');
      alert('Unable to fetch Firebase Info');
    }
  }

  const ShowConfirmDeletionPopup = () => {
    setDeleteModalVisible(true);
  }

  const deleteAccountConfirmed = async () => {
    try {
      await API.deleteUserAccount(userId, deleteAccountReason);
      setDeleteModalVisible(false);
      refreshAccount();
      //goback handler
    } catch (error) {
      message.error('Unable to delete Account');
    } finally {
      setDeleteModalVisible(false);
    }
  }

  const closeDeleteModal = () => {
    setDeleteAccountReason(null);
    setDeleteModalVisible(false);
  }
  
  const allCharges = async () => {
    const chargesResponse = await API.getStripeUserCharges({ userId });
    setCharges(chargesResponse);
    chargesResponse.map((charge, i) => charge.disputed ? setTimeout(() => getDispute(charge.id), UPDATE_TIMEOUT * i) : null)
  }

  const allInvoices = async () => {
    const invoicesResponse = await API.getStripeUserInvoices({ userId });
    setInvoices(invoicesResponse);
  }

  const getCustomer = async () => {
    const customerResponse = await API.getStripeCustomer({ userId });
    setCustomer(customerResponse);
  }

  const getDispute = async (chargeId) => {
    const disputeResponse = await API.getStripeUserDisputes({ userId, chargeId });
    setDisputes((disputes) => ({ ...disputes, [chargeId]: disputeResponse }));
  }

  const getCcAccounts = async () => {
    const ccAccountsResponse = await API.getCreditCardsLegacy({ userId });
    setCcAccounts(ccAccountsResponse);
  }

  const getCCDomains = async () => {
    const domainResponse = await API.getCreditCards({ userId });
    setCcDomain(domainResponse?.cards || []);
  }

  // Hide the previous user's details while the next user is loading
  const headerUser = loading ? {} : summaryData.userSummary;
  const headerEmptyValue = loading ? 'Loading...' : 'N/A';
  const headerName = [headerUser?.firstName, headerUser?.lastName].filter(Boolean).join(' ');

  useEffect(() => {
    document.title = headerName ? `${headerName} - ${APP_TITLE}` : APP_TITLE;
    return () => {
      document.title = APP_TITLE;
    };
  }, [headerName]);

  // Profile Summary already shows these details, so only the other tabs get the header
  const renderTabBar = (props, DefaultTabBar) => (
    <>
      <StickyTabBar anchorRef={tabBarAnchorRef}>
        <DefaultTabBar {...props} />
      </StickyTabBar>
      {activeTab !== PROFILE_SUMMARY_TAB &&
        <Space
          wrap
          split={<Divider type="vertical" />}
          style={{ display: 'flex', paddingBottom: 12, marginBottom: 16, borderBottom: '1px solid #f0f0f0' }}>
          <Text strong>{headerName || headerEmptyValue}</Text>
          <Text type="secondary" copyable>{userId}</Text>
          <Text type="secondary" copyable={!!headerUser?.email}>{headerUser?.email || headerEmptyValue}</Text>
        </Space>
      }
    </>
  );

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>

        <Col xs="24" xl={24} style={{ position: 'relative' }}>
          {/* <Collapse defaultActiveKey={[1, 2, 3, 4, 5]}> */}
          <Card>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              {isDev() &&
                  <Link to={`/user-json/${userId}`} className="font-bold view-as-json" target="_blank">
                    View as JSON
                  </Link>
              }
              <RetweetOutlined title="Refresh" onClick={refreshAccount} style={{ fontSize: 20, marginLeft: 10, cursor: 'pointer' }} />
            </div>
            <Tabs activeKey={activeTab} onChange={changeTab} renderTabBar={renderTabBar}>
              <TabPane tab="Profile Summary" key={PROFILE_SUMMARY_TAB}>
                <ProfileSummary
                  key={refreshKey}
                  userSummary={summaryData}
                  creditCards={creditCards}
                  loading={loading}
                  refreshList={refreshAccount}
                  firebaseInfo={firebaseInfo}
                  autoPayStatus={autoPayStatus} />
              </TabPane>
              <TabPane tab="Subscription Details" key="2">
                <SubscriptionDetails
                  key={refreshKey}
                  summaryData={summaryData}
                  loading={loading}
                  getData={refreshAccount}
                  userId={userId}
                  invoices={invoices}
                  customer={customer}
                  charges={charges}
                  disputes={disputes}
                  />
              </TabPane>
              <TabPane tab="Plaid" key="3">
                <PlaidDetails key={refreshKey} userId={userId} refreshAccount={refreshAccount} />
              </TabPane>
              <TabPane tab="Ava Products" key="4">
                <Products
                  key={refreshKey}
                  loading={loading}
                  getData={refreshAccount}
                  summaryData={summaryData}
                  creditCards={creditCards}
                  ccDomains={ccDomains}
                  userId={userId}
                  getAllCreditCardData={refreshAccount}
                  loanDetails={loanDetails}
                  paymentAttempts={paymentAttempts}
                  autoPayStatus={autoPayStatus} 
                  />
              </TabPane>
              <TabPane tab="Money Transactions" key="5">
                <MoneyTransactions
                  key={refreshKey}
                  merchantList={summaryData.moneyTransactions}
                  loading={loading}
                  refreshList={refreshAccount}
                  charges={charges}
                  disputes={disputes}
                  />
              </TabPane>

              <TabPane tab="Referrals" key="6">
                <Referrals key={refreshKey} data={summaryData?.userSummary?.referralResponse} loading={loading} />
              </TabPane>

              <TabPane tab="Furnishment Details" key="8">
                <FurnishmentDetails
                  key={refreshKey}
                  refreshAccount={refreshAccount}
                  userId={userId}
                  userSummary={summaryData.userSummary}
                  furnishmentDetails={summaryData?.furnishingResponse}
                  creditCards={summaryData.creditCards}
                  loading={loading} />
              </TabPane>
              <TabPane tab="App Open Dates" key="9">
                <FirebaseDeviceEvents key={refreshKey} userId={userId} />
              </TabPane>
              {/* <TabPane tab="Rent Reporting" key="11">
                <RentReportingComponent userId={userId} />
              </TabPane> */}
            </Tabs>
          </Card>

        </Col>

        {(isSupportManager() && summaryData.userSummary && summaryData.userSummary.status !== 'DELETED' && !loading) &&
            <Col xs={24} xl={24} sm={24} md={24} style={{ justifyContent: 'center', alignItems: 'center', display: 'flex', marginTop: 20 }}>
              <Button
                  onClick={ShowConfirmDeletionPopup}
                  style={{ width: 300, height: 60 }} >
                {'Delete Account'}
              </Button>
            </Col>
        }

        <Modal
          title={'Confirmation'}
          centered
          mask={true}
          maskClosable={true}
          destroyOnClose={true}
          okButtonProps={{
            disabled: !deleteAccountReason
          }
          }
          closable={true}
          visible={deleteModalVisible}
          onOk={deleteAccountConfirmed}
          onCancel={closeDeleteModal}
          className="confirm-modal"
        >
          <p>Please provide the reason for deleting this account?</p>
          <Input
            onChange={(e) => setDeleteAccountReason(e.target.value)}
            placeholder={"Reason..."}
            value={deleteAccountReason} />
        </Modal>
      </Row>
    </div>
  );
}

export default AccountSummary;


const initialDataState = { userSummary: {}, accountDetails: {}, saveAndBuildCredit: [], plaidAccount: [], creditCards: [], merchantList: [] };